import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Buildings, ArrowRight } from "@phosphor-icons/react";

/**
 * Landlord/Owner Portal Login — enter email OR owner name, server returns
 * the magic token URL → redirect. Mirrors TenantLogin structure.
 * URL: /landlord/login
 */
export default function LandlordLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [name,  setName]  = useState("");
  const [busy,  setBusy]  = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() && !name.trim()) { toast.error("Enter email or your name"); return; }
    setBusy(true);
    try {
      const r = await api.post("/public/landlord-portal-lookup",
        { email: email.trim() || null, name: name.trim() || null });
      const url = r.data?.portal_url;
      if (!url) throw new Error("No portal URL returned");
      const path = url.startsWith("http") ? new URL(url).pathname : url;
      toast.success("Opening your owner portal…");
      nav(path);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Owner not found — contact your property manager"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1220] text-white flex flex-col" data-testid="landlord-login-page">
      <div className="flex-1 flex items-center justify-center p-5">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-6 justify-center">
            <Logo size={30}/>
            <span className="font-display font-black text-2xl tracking-tight">Field Radar</span>
          </div>
          <div className="bg-white text-slate-900 p-6 border-2 border-fuchsia-500 rounded">
            <div className="flex items-center gap-2 mb-1">
              <Buildings weight="fill" size={22} className="text-fuchsia-600"/>
              <h1 className="font-display font-black text-2xl tracking-tight">Owner Portal</h1>
            </div>
            <p className="text-sm text-slate-600 mb-5 leading-snug">
              Enter the email on file (or your owner name) to open your properties dashboard. See open work orders, before/after photos, and job status.
            </p>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Email</label>
                <input value={email} onChange={e => setEmail(e.target.value)} type="email"
                  placeholder="leslie@lasalle.edu"
                  autoFocus
                  data-testid="landlord-login-email"
                  className="w-full h-12 px-3 border-2 border-slate-300 focus:border-fuchsia-500 focus:outline-none text-sm"/>
              </div>
              <div className="text-center text-[10px] font-black uppercase tracking-widest text-slate-400">— or —</div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Owner Name</label>
                <input value={name} onChange={e => setName(e.target.value)}
                  placeholder="LaSalle University"
                  data-testid="landlord-login-name"
                  className="w-full h-12 px-3 border-2 border-slate-300 focus:border-fuchsia-500 focus:outline-none text-sm"/>
              </div>
              <button type="submit" disabled={busy}
                data-testid="landlord-login-submit"
                className="w-full h-12 bg-fuchsia-600 hover:bg-fuchsia-700 disabled:bg-slate-400 text-white font-black uppercase text-xs tracking-widest border-2 border-[#0f172a] inline-flex items-center justify-center gap-2 transition">
                {busy ? "Looking up…" : <>Open My Portal <ArrowRight weight="bold" size={16}/></>}
              </button>
            </form>
            <div className="mt-5 pt-4 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
              Resident? Use the <a href="/tenant/login" className="text-cyan-700 font-bold hover:underline">Resident Portal</a> instead.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
